// src/services/championshipManagerService.js
import { configuredAxios as api } from './axiosConfig';

const ChampionshipManagerService = {
  /**
   * Adiciona um novo gestor ao campeonato
   * @param {string} championshipId - ID ou slug do campeonato
   * @param {string} email - Email do novo gestor
   * @returns {Promise<{success: boolean, managers?: Array, message?: string, error?: string, status?: number}>}
   */
  addManager: async (championshipId, email) => {
    if (!championshipId || !email) {
      console.error('Missing parameters');
      return {
        success: false,
        error: 'ID do campeonato e email são obrigatórios'
      };
    }

    try {
      const response = await api.post(`/championships/${championshipId}/managers`, { email });
      
      // Verificação mais robusta da resposta
      if (!response.data) {
        throw new Error('Resposta da API sem dados');
      }

      return {
        success: true,
        // Garante que os dados do gestor, incluindo o telefone e avatar, sejam retornados corretamente após a adição.
        // O backend precisará garantir que os gestores retornados por esta rota incluam o campo 'phone' e 'avatar'.
        // O creator também é passado para manter o estado do componente atualizado.
        data: { 
          managers: Array.isArray(response.data.managers) ? response.data.managers : [],
          creator: response.data.creator 
        }, 
        message: 'Gestor adicionado com sucesso'
      };
    } catch (error) {
      console.error('Error adding manager:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao adicionar gestor',
        status: error.response?.status || 500
      };
    }
  },

  /**
   * Remove um gestor do campeonato
   * @param {string} championshipId - ID ou slug do campeonato
   * @param {string} managerId - ID do gestor a ser removido
   * @returns {Promise<{success: boolean, managers?: Array, message?: string, error?: string, status?: number}>}
   */
  removeManager: async (championshipId, managerId) => {
    if (!championshipId || !managerId) {
      console.error('Missing parameters');
      return {
        success: false,
        error: 'ID do campeonato e ID do gestor são obrigatórios'
      };
    }

    try {
      const response = await api.delete(`/championships/${championshipId}/managers/${managerId}`);
      
      // Verificação mais robusta da resposta
      if (!response.data) {
        throw new Error('Resposta da API sem dados');
      }

      return {
        success: true,
        // Garante que os dados do gestor, incluindo o telefone e avatar, sejam retornados corretamente após a remoção.
        // O backend precisará garantir que os gestores retornados por esta rota incluam o campo 'phone' e 'avatar'.
        // O creator também é passado para manter o estado do componente atualizado.
        data: { 
          managers: Array.isArray(response.data.managers) ? response.data.managers : [],
          creator: response.data.creator 
        },
        message: 'Gestor removido com sucesso'
      };
    } catch (error) {
      console.error('Error removing manager:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao remover gestor',
        status: error.response?.status || 500
      };
    }
  },

  /**
   * Obtém a lista de gestores do campeonato
   * @param {string} championshipId - ID ou slug do campeonato
   * @returns {Promise<{success: boolean, managers?: Array, error?: string, status?: number}>}
   */
  getManagers: async (championshipId) => {
    if (!championshipId) {
      console.error('Missing championship ID');
      return {
        success: false,
        error: 'ID do campeonato é obrigatório'
      };
    }

    try {
      // IMPORTANTE: Seu backend PRECISA garantir que o campo 'phone' e 'avatar'
      // seja incluído na resposta para cada gestor e para o criador.
      // Por exemplo, usando .populate('managers', 'name email phone avatar') no Mongoose.
      const response = await api.get(`/championships/${championshipId}/managers`);
      
      // Verificação mais robusta da resposta
      if (!response.data) {
        throw new Error('Resposta da API sem dados');
      }

      return { // Retorna managers E creator para o frontend
        success: true,
        managers: Array.isArray(response.data.managers) ? response.data.managers : [],
        creator: response.data.creator // Assumindo que o backend retorna 'creator' no objeto de resposta
      };
    } catch (error) {
      console.error('Error fetching managers:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Erro ao buscar gestores',
        status: error.response?.status || 500
      };
    }
  }
};

export default ChampionshipManagerService;